import { SowInput } from './ValidationFields';
import { Button } from '../Button';
import { fmt } from '../../lib/format';
import type { SowServiceGroup } from '../../types';

interface Props {
  groups: SowServiceGroup[];
  total: number;
  onChange: (groups: SowServiceGroup[]) => void;
  onTotalChange: (total: number) => void;
}

/**
 * SERVICES PURCHASED editor. Groups (e.g. "Program Learning Journey –
 * Cohort 1") contain line rows (title / delivery line / price). The prefill
 * only DRAFTS this from Salesforce products — rows are fully editable, and the
 * total can be overridden (with a mismatch warning, since the contract prints
 * whatever is entered).
 */
export function ServicesTableEditor({ groups, total, onChange, onTotalChange }: Props): JSX.Element {
  const linesSum = Math.round(groups.flatMap((g) => g.lines).reduce((s, l) => s + (Number.isFinite(l.price) ? l.price : 0), 0) * 100) / 100;
  const mismatch = Math.abs(linesSum - total) > 0.005;

  const updateGroup = (gi: number, patch: Partial<SowServiceGroup>) =>
    onChange(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));

  const updateLine = (gi: number, li: number, patch: Partial<SowServiceGroup['lines'][number]>) =>
    onChange(
      groups.map((g, i) =>
        i === gi ? { ...g, lines: g.lines.map((l, j) => (j === li ? { ...l, ...patch } : l)) } : g,
      ),
    );

  return (
    <div>
      {groups.map((g, gi) => (
        <div key={gi} className="card" style={{ marginBottom: 10 }}>
          <div className="card-hd" style={{ gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, flex: 1 }}>
              <SowInput
                className="input"
                placeholder='Group title (e.g. "Program Learning Journey – Cohort 1")'
                name={`groups.${gi}.title`} value={g.title}
                onChange={(e) => updateGroup(gi, { title: e.target.value })}
              />
              <SowInput
                className="input"
                placeholder='Subtitle (e.g. "Up to 15 Participants | 1 Facilitator")'
                name={`groups.${gi}.subtitle`} value={g.subtitle}
                onChange={(e) => updateGroup(gi, { subtitle: e.target.value })}
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              icon="trash"
              title="Remove group"
              onClick={() => onChange(groups.filter((_, i) => i !== gi))}
            >
              {''}
            </Button>
          </div>
          <div className="card-bd" style={{ padding: 12 }}>
            {g.lines.map((l, li) => (
              <div
                key={li}
                style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 120px auto', gap: 8, marginBottom: 8, alignItems: 'center' }}
              >
                <SowInput
                  className="input"
                  placeholder="Program title (italic line in the contract)"
                  name={`groups.${gi}.lines.${li}.title`} value={l.title}
                  onChange={(e) => updateLine(gi, li, { title: e.target.value })}
                />
                <SowInput
                  className="input"
                  placeholder='Delivery ("In-Person Program | Delivery: July 6, 2026 in Lisbon, Portugal")'
                  name={`groups.${gi}.lines.${li}.deliveryLine`} value={l.deliveryLine}
                  onChange={(e) => updateLine(gi, li, { deliveryLine: e.target.value })}
                />
                <SowInput
                  className="input d-tnum"
                  type="number"
                  min={0}
                  step={25}
                  name={`groups.${gi}.lines.${li}.price`} value={Number.isFinite(l.price) ? l.price : ''}
                  onChange={(e) => updateLine(gi, li, { price: e.target.value === '' ? 0 : Number(e.target.value) })}
                  aria-label="Price (USD)"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon="trash"
                  title="Remove line"
                  onClick={() => updateGroup(gi, { lines: g.lines.filter((_, j) => j !== li) })}
                >
                  {''}
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="secondary"
              icon="plus"
              onClick={() => updateGroup(gi, { lines: [...g.lines, { title: '', deliveryLine: '', price: 0 }] })}
            >
              Add line
            </Button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          size="sm"
          variant="secondary"
          icon="plus"
          onClick={() =>
            onChange([
              ...groups,
              {
                title: `Program Learning Journey – Cohort ${groups.length + 1}`,
                subtitle: 'Up to 15 Participants | 1 Facilitator',
                lines: [{ title: '', deliveryLine: '', price: 0 }],
              },
            ])
          }
        >
          Add cohort group
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label className="input-label" style={{ marginBottom: 0 }}>Contract total (USD)</label>
          <SowInput
            className="input d-tnum"
            style={{ width: 140 }}
            type="number"
            min={0}
            step={25}
            name="total" value={Number.isFinite(total) ? total : ''}
            onChange={(e) => onTotalChange(e.target.value === '' ? 0 : Number(e.target.value))}
          />
        </div>
      </div>

      {mismatch && (
        <div className="banner warn" style={{ marginTop: 8 }}>
          <span className="dot" />
          <span>
            Line prices add up to <strong>{fmt.curFull(linesSum)}</strong> but the contract total is{' '}
            <strong>{fmt.curFull(total)}</strong> — the document prints the total as entered.
          </span>
        </div>
      )}
    </div>
  );
}
